import {
  cn,
  Avatar as AvatarBase,
  AvatarImage,
  AvatarFallback,
} from "web-design-system";
import { cva, VariantProps } from "class-variance-authority";

export const sizeVariants = cva("rounded-lg", {
  variants: {
    size: {
      default: "w-10 h-10 text-sm",
      md: "w-9 h-9 text-sm",
      sm: "w-8 h-8 text-sm",
      lg: "w-12 h-12 text-base",
      xs: "w-7 h-7 rounded-sm text-xs",
      xl: "w-16 h-16",
    },
  },
});

export const bgVariants = cva("", {
  variants: {
    color: {
      red: "bg-name-tag-red",
      yellow: "bg-name-tag-yellow",
      amber: "bg-name-tag-amber",
      green: "bg-name-tag-green",
      violet: "bg-name-tag-violet",
      blue: "bg-name-tag-blue",
      fuchsia: "bg-name-tag-fuchsia",
      emerald: "bg-name-tag-emerald",
      rose: "bg-name-tag-rose",
    },
  },
  defaultVariants: {
    color: "violet",
  },
});

export const textVariants = cva("", {
  variants: {
    color: {
      red: "text-name-tag-red-foreground",
      yellow: "text-name-tag-yellow-foreground",
      amber: "text-name-tag-amber-foreground",
      green: "text-name-tag-green-foreground",
      violet: "text-name-tag-violet-foreground",
      blue: "text-name-tag-blue-foreground",
      fuchsia: "text-name-tag-fuchsia-foreground",
      emerald: "text-name-tag-emerald-foreground",
      rose: "text-name-tag-rose-foreground",
    },
  },
  defaultVariants: {
    color: "violet",
  },
});

export type AvatarClientColor = VariantProps<typeof bgVariants>["color"];

// Cycles back to the first color once every entry has been used (see
// buildClientAvatarColorMap in ../utils/client-avatar-colors.ts). "grey"/
// "slate" are omitted as near-duplicates of white/each other; the rest are
// picked for maximum visual distinction from each other.
export const CLIENT_AVATAR_COLORS: AvatarClientColor[] = [
  "violet",
  "red",
  "yellow",
  "green",
  "blue",
  "fuchsia",
  "emerald",
  "rose",
  "amber",
];

export function Avatar({
  url,
  fallback,
  size,
  color,
  className,
  variant = "client",
}: {
  url?: string;
  fallback: string;
  size?: VariantProps<typeof sizeVariants>["size"];
  color?: VariantProps<typeof bgVariants>["color"];
  className?: string;
  variant?: "amc-logo" | "client";
}) {
  return (
    <AvatarBase
      className={cn(
        sizeVariants({ size }),
        bgVariants({ color }),
        textVariants({ color }),
        className,
        variant === "amc-logo" &&
          "bg-secondary dark:bg-secondary-foreground text-secondary-foreground dark:text-secondary"
      )}
    >
      <AvatarImage className="size-full bg-background" src={url} />
      <AvatarFallback className="font-semibold bg-transparent">
        {fallback}
      </AvatarFallback>
    </AvatarBase>
  );
}
